import { useContext, useCallback, useMemo } from 'react';

import { trackAction } from '~/libs/analytics';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { AppContext } from '~/contexts/AppContext';
import { useDesktop } from '~/hooks/useMediaQuery';
import { ORG_COOKIE_KEY } from '~/utils/global';

/**
 * This list is not meant to be exaustive, feel free to add your own.
 */
export enum Action {
  clicked = 'clicked',
  hovered = 'hovered',
  viewed = 'viewed',
  opened = 'opened',
  closed = 'closed',
  scrolled = 'scrolled',
  submitted = 'submitted',
  changed = 'changed',
}

/**
 * Thin wrapper function around @circleci/analytics trackAction method
 * that ensures we are sending standardized analytics events
 * following a Prefix => Context => Object pattern.
 *
 * Note that this function is also attaching automatically the following properties:
 * orgId (string), page (string), hash (string), locale (string), desktop (boolean)
 *
 * @param object  Name of something that represents an UI/UX element.
 * @param action  Verb representing the action taken on that element (clicked, hoverd, ...).
 * @param properties  A bag of metadata to send to Segment/Amplitude.
 */
const trackStandardizedAction: (
  object: string,
  action: Action,
  properties?: object,
) => void = (object, action, properties = {}): void => {
  const prefix = 'dd';
  const context = 'devhub';

  trackAction(`${prefix}_${context}_${object}`, { ...properties, action });
};

export const useAnalytics = () => {
  const router = useRouter();
  const { locale } = useContext(AppContext);
  const isDesktop = useDesktop();

  const orgId = Cookies.get(ORG_COOKIE_KEY) ?? null;

  const { page, hash } = useMemo(() => {
    if (!router) {
      return { page: '', hash: '' };
    }
    // transforms a doc link into a page identifier
    // for example: https://ui.circleci.com/docs/2.0/migrating-from-github/
    // will be transformed into 'migrating-from-github'
    const pageParts =
      router.asPath === '/'
        ? ['homepage']
        : router.asPath.replace('/2.0/', '').split('#');

    const page = pageParts[0];
    const hash = pageParts.length === 2 ? pageParts[1] : null;

    return { page, hash };
  }, [router]);

  const trackStandardized = useCallback(
    (object: string, action: Action, properties?: object) => {
      properties = properties ?? {};

      trackStandardizedAction(object, action, {
        orgId,
        page: page,
        hash: hash,
        locale: locale,
        desktop: isDesktop,
        ...properties,
      });
    },
    [orgId, page, hash, locale, isDesktop],
  );

  const track = useCallback(
    (name: string, properties?: object) => {
      properties = properties ?? {};

      trackAction(name, {
        orgId,
        page: page,
        hash: hash,
        locale: locale,
        desktop: isDesktop,
        ...properties,
      });
    },
    [orgId, page, hash, locale, isDesktop],
  );

  return { trackStandardizedAction: trackStandardized, trackAction: track };
};
